import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Key, 
  Plus, 
  Copy, 
  Trash2, 
  Eye,
  EyeOff,
  ExternalLink,
  Globe,
  Shield,
  Activity,
  Database,
  Code,
  FileText,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const ExternalAPIManager = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [hmacCredentials, setHmacCredentials] = useState([]);
  const [showHmacForm, setShowHmacForm] = useState(false);
  const [newHmacCreds, setNewHmacCreds] = useState(null);
  const [showHmacSecret, setShowHmacSecret] = useState(false);
  const [showWebhookForm, setShowWebhookForm] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  
  const [hmacForm, setHmacForm] = useState({
    name: '',
    permissions: {
      backups: true,
      exports: true,
      analytics: false
    },
    allowed_ips: ''
  });

  const [keyForm, setKeyForm] = useState({
    name: '',
    permissions: {
      members: true,
      downlines: true,
      matrix: true,
      pif: true,
      leads: true,
      transactions: true,
      analytics: false,
      webhooks: false
    }
  });

  const [webhookForm, setWebhookForm] = useState({
    name: '',
    url: '',
    events: {
      member_created: true,
      member_updated: true,
      downline_added: true,
      matrix_updated: true,
      lead_captured: true,
      pif_sponsored: true
    }
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    loadApiKeys();
    loadWebhooks();
    loadHmacCredentials();
  }, []);

  const loadHmacCredentials = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/hmac-credentials`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHmacCredentials(data.credentials || []);
      }
    } catch (error) {
      console.error('Error loading HMAC credentials:', error);
    }
  };

  const loadApiKeys = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/external-api/keys`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setApiKeys(data.api_keys || []);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load API keys",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWebhooks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/external-api/webhooks`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks || []);
      }
    } catch (error) {
      console.error('Error loading webhooks:', error);
    }
  };

  const createApiKey = async () => {
    try {
      const permissions = Object.keys(keyForm.permissions).filter(
        key => keyForm.permissions[key]
      );

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/external-api/create-key`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: keyForm.name,
          permissions: permissions
        })
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setNewApiKey(data.api_key_info);
        setShowCreateForm(false);
        setKeyForm({
          name: '',
          permissions: {
            members: true,
            downlines: true,
            matrix: true,
            pif: true,
            leads: true,
            transactions: true,
            analytics: false,
            webhooks: false
          }
        });
        
        toast({
          title: "Success",
          description: "API key created successfully",
          variant: "default"
        });
        
        loadApiKeys();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to create API key",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error creating API key",
        variant: "destructive"
      });
    }
  };

  const createHmacCredentials = async () => {
    try {
      const permissions = Object.keys(hmacForm.permissions).filter(
        key => hmacForm.permissions[key]
      );

      const allowed_ips = hmacForm.allowed_ips 
        ? hmacForm.allowed_ips.split(',').map(ip => ip.trim()).filter(ip => ip)
        : [];

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/hmac-credentials/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: hmacForm.name,
          permissions: permissions,
          allowed_ips: allowed_ips
        })
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        setNewHmacCreds(data.credentials);
        setShowHmacForm(false);
        setHmacForm({
          name: '',
          permissions: {
            backups: true,
            exports: true,
            analytics: false
          },
          allowed_ips: ''
        });
        
        toast({
          title: "Success",
          description: "HMAC credentials created successfully",
          variant: "default"
        });
        
        loadHmacCredentials();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to create HMAC credentials",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error creating HMAC credentials",
        variant: "destructive"
      });
    }
  };

  const deactivateApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to deactivate this API key? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/external-api/keys/${keyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast({
          title: "Success",
          description: "API key deactivated successfully",
          variant: "default"
        });
        loadApiKeys();
      } else {
        toast({
          title: "Error",
          description: "Failed to deactivate API key",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error deactivating API key",
        variant: "destructive"
      });
    }
  };

  const createWebhook = async () => {
    try {
      const events = Object.keys(webhookForm.events).filter(
        key => webhookForm.events[key]
      );

      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/external-api/webhooks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: webhookForm.name,
          url: webhookForm.url,
          events: events
        })
      });

      const data = await response.json();

      if (response.ok && data.ok) {
        toast({
          title: "Success",
          description: "Webhook created successfully",
          variant: "default"
        });
        
        setShowWebhookForm(false);
        setWebhookForm({
          name: '',
          url: '',
          events: {
            member_created: true,
            member_updated: true,
            downline_added: true,
            matrix_updated: true,
            lead_captured: true,
            pif_sponsored: true
          }
        });
        
        loadWebhooks();
      } else {
        toast({
          title: "Error",
          description: data.detail || "Failed to create webhook",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Network error creating webhook",
        variant: "destructive"
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({
        title: "Copied",
        description: "API key copied to clipboard",
        variant: "default"
      });
    });
  };

  const downloadIntegrationGuide = () => {
    // This would download the PHP integration guide
    const guideUrl = `${backendUrl}/api/admin/backup/download/PHP_INTEGRATION_GUIDE.md`;
    window.open(guideUrl, '_blank');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatPermissions = (permissions) => {
    return permissions.join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">External API Management</h2>
          <p className="text-muted-foreground">
            Manage API access for PHP backup system and external integrations
          </p>
        </div>
        <div className="flex space-x-2">
          <Button 
            onClick={downloadIntegrationGuide}
            variant="outline"
          >
            <Download className="h-4 w-4 mr-2" />
            Integration Guide
          </Button>
          <Button 
            onClick={() => setShowHmacForm(true)}
            variant="default"
          >
            <Key className="h-4 w-4 mr-2" />
            Create HMAC Keys
          </Button>
          <Button 
            onClick={() => setShowWebhookForm(true)}
            variant="outline"
          >
            <Globe className="h-4 w-4 mr-2" />
            Add Webhook
          </Button>
          <Button 
            onClick={() => setShowCreateForm(true)}
            variant="outline"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create API Key
          </Button>
        </div>
      </div>

      {/* New API Key Display */}
      {newApiKey && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center text-green-800">
              <Key className="h-5 w-5 mr-2" />
              New API Key Created
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white p-4 rounded border">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium">API Key:</p>
                    <p className="font-mono text-sm bg-gray-100 p-2 rounded mt-1">
                      {showApiKey ? newApiKey.api_key : '••••••••••••••••••••••••••••••••'}
                    </p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(newApiKey.api_key)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Important:</strong> Save this API key securely. You won't be able to see it again!
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p><strong>Key ID:</strong> {newApiKey.key_id}</p>
                  <p><strong>Created:</strong> {formatDate(newApiKey.created_at)}</p>
                </div>
                <div>
                  <p><strong>Permissions:</strong> {formatPermissions(newApiKey.permissions)}</p>
                </div>
              </div>

              <Button 
                onClick={() => setNewApiKey(null)}
                variant="outline"
                className="w-full"
              >
                Dismiss
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create API Key Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create New API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium">API Key Name</label>
              <Input
                value={keyForm.name}
                onChange={(e) => setKeyForm(prev => ({...prev, name: e.target.value}))}
                placeholder="PHP Backup System"
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium mb-3 block">Permissions</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.keys(keyForm.permissions).map(permission => (
                  <div key={permission} className="flex items-center space-x-2">
                    <Switch
                      checked={keyForm.permissions[permission]}
                      onCheckedChange={(checked) => 
                        setKeyForm(prev => ({
                          ...prev,
                          permissions: {...prev.permissions, [permission]: checked}
                        }))
                      }
                    />
                    <label className="text-sm capitalize">
                      {permission.replace('_', ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-2">
              <Button onClick={createApiKey} disabled={!keyForm.name}>
                Create API Key
              </Button>
              <Button 
                onClick={() => setShowCreateForm(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Webhook Form */}
      {showWebhookForm && (
        <Card>
          <CardHeader>
            <CardTitle>Create Webhook</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Webhook Name</label>
                <Input
                  value={webhookForm.name}
                  onChange={(e) => setWebhookForm(prev => ({...prev, name: e.target.value}))}
                  placeholder="PHP System Webhook"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Webhook URL</label>
                <Input
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm(prev => ({...prev, url: e.target.value}))}
                  placeholder="https://your-php-system.com/webhook/bbc"
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-3 block">Events</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {Object.keys(webhookForm.events).map(event => (
                  <div key={event} className="flex items-center space-x-2">
                    <Switch
                      checked={webhookForm.events[event]}
                      onCheckedChange={(checked) => 
                        setWebhookForm(prev => ({
                          ...prev,
                          events: {...prev.events, [event]: checked}
                        }))
                      }
                    />
                    <label className="text-sm">
                      {event.replace('_', ' ')}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-2">
              <Button 
                onClick={createWebhook} 
                disabled={!webhookForm.name || !webhookForm.url}
              >
                Create Webhook
              </Button>
              <Button 
                onClick={() => setShowWebhookForm(false)}
                variant="outline"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* API Keys List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Key className="h-5 w-5 mr-2" />
            API Keys
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <Activity className="h-8 w-8 animate-spin mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">Loading API keys...</p>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-8">
              <Key className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No API keys created yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium">{key.name}</h3>
                        <Badge variant={key.is_active ? "default" : "secondary"}>
                          {key.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground space-y-1">
                        <p><strong>Key ID:</strong> {key.id}</p>
                        <p><strong>Created:</strong> {formatDate(key.created_at)}</p>
                        {key.last_used && (
                          <p><strong>Last Used:</strong> {formatDate(key.last_used)}</p>
                        )}
                        <p><strong>Usage Count:</strong> {key.usage_count}</p>
                        <p><strong>Permissions:</strong> {formatPermissions(key.permissions)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deactivateApiKey(key.id)}
                        disabled={!key.is_active}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Webhooks List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Webhooks
          </CardTitle>
        </CardHeader>
        <CardContent>
          {webhooks.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-muted-foreground">No webhooks configured yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {webhooks.map((webhook, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="font-medium">{webhook.name}</h3>
                        <Badge variant={webhook.is_active ? "default" : "secondary"}>
                          {webhook.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                      <div className="mt-2 text-sm text-muted-foreground space-y-1">
                        <p><strong>URL:</strong> {webhook.url}</p>
                        <p><strong>Events:</strong> {webhook.events.join(', ')}</p>
                        <p><strong>Deliveries:</strong> {webhook.delivery_count} (Failed: {webhook.failed_deliveries})</p>
                        {webhook.last_delivery && (
                          <p><strong>Last Delivery:</strong> {formatDate(webhook.last_delivery)}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(webhook.url, '_blank')}
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Integration Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Code className="h-5 w-5 mr-2" />
            Integration Instructions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2">📡 Base API URL</h4>
                <p className="text-sm bg-gray-100 p-2 rounded font-mono">
                  {backendUrl}/api/external
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-2">🔐 Authentication</h4>
                <p className="text-sm bg-gray-100 p-2 rounded font-mono">
                  Authorization: Bearer {'{api_key}'}
                </p>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">🚀 Key Endpoints</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="font-mono">POST /sync/full</span>
                  <span className="text-muted-foreground">Complete data sync</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">GET /member/{'{id}'}</span>
                  <span className="text-muted-foreground">Member details</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">GET /members/list</span>
                  <span className="text-muted-foreground">Paginated members</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-mono">GET /downlines/{'{id}'}</span>
                  <span className="text-muted-foreground">Member downline</span>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-3">
              <p className="text-sm text-blue-800">
                <strong>Download the complete PHP Integration Guide</strong> for detailed implementation examples, 
                database schemas, and best practices for integrating with your PHP 8.3 backup system.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalAPIManager;